import { stringify } from 'querystring'

import React from 'react'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Shimmer } from '@wartek-id/shimmer'

import { GATrackEvent } from 'utils/googleAnalytics'
import { useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'

interface AksiNyataCtaCardProps {
  href: string
  title: string
  author: string
  image: string
  order: number
  aksiNyataId: string
  school?: string
}

const AksiNyataCTACard = ({
  href,
  order,
  aksiNyataId,
  title,
  author,
  school,
  image,
}: AksiNyataCtaCardProps) => {
  const trackClick = () => {
    GATrackEvent({
      eventName: 'learning_activity',
      eventParams: {
        event_trigger: 'topic_list_page_with_aksinyata',
        action_type: 'click_curated_list',
        meta: JSON.stringify({
          aksi_nyata_id: aksiNyataId,
          order: order,
          is_variant: 1,
        }),
      },
      sendDefaultParams: true,
    })
  }

  return (
    <div className="relative flex py-3 border-b border-gray-30 last:border-0">
      <div className="flex-1 mr-3">
        <Link href={href} passHref>
          <Text
            onClick={trackClick}
            as="a"
            variant="body-sm"
            className="line-clamp-2 ellipsis overlay-link"
          >
            <strong>{title}</strong>
          </Text>
        </Link>
        <div>
          <Text className="inline-block" variant="helper">
            {author}
          </Text>
          {!!school && (
            <Text className="inline-block" variant="helper">
              ,{school}
            </Text>
          )}
        </div>
      </div>
      <div className="flex-grow-0 flex-shrink-0 w-[92px] h-[60px] rounded overflow-hidden">
        <img
          className="object-center object-cover w-[92px] h-[60px]"
          src={image}
          alt=""
        />
      </div>
    </div>
  )
}

const AksiNyataCtaShimmer = () => {
  return (
    <div className="relative flex py-3 w-full border-b border-gray-30 last:border-0">
      <div className="flex-grow-1 w-full mr-3">
        <Shimmer className="mb-4" height={16} style={{ width: '100%' }} />
        <Shimmer className="mb-4" height={16} style={{ width: '100%' }} />
        <Shimmer style={{ width: '100%' }} height={16} />
      </div>
      <div className="flex-grow-0 flex-shrink-0 w-[92px] h-[60px] rounded overflow-hidden">
        <Shimmer height={60} width={60} />
      </div>
    </div>
  )
}

const TopicListCallToAction = () => {
  const { data, status: fetchStatus } = useGetKeyPairValue('aksinyata_cta_ab')

  return (
    <div className="-mx-4 p-4 bg-[#EFF0F0]">
      <Text className="mb-3" variant="heading-sm">
        Lihat hasil penerapan materi Pelatihan Mandiri pendidik lain lewat Aksi
        Nyata ini
      </Text>
      <div>
        {fetchStatus === 'loading' && (
          <>
            <AksiNyataCtaShimmer />
            <AksiNyataCtaShimmer />
            <AksiNyataCtaShimmer />
          </>
        )}

        {fetchStatus === 'success' &&
          !!data.data?.cta &&
          data.data?.cta.map(
            (
              {
                author,
                id,
                image,
                order,
                title,
                topik_name: topikName,
                topik_id: topikId,
              },
              index
            ) => (
              <AksiNyataCTACard
                key={index}
                href={
                  `/pelatihan-mandiri/aksi-nyata/${id}?` +
                  stringify({
                    topik_id: topikId,
                    topik_name: topikName,
                  })
                }
                title={title}
                author={author}
                image={image}
                aksiNyataId={id as string}
                order={order}
              />
            )
          )}
        {(fetchStatus === 'error' ||
          (fetchStatus === 'success' && !data?.data?.cta)) && (
          <div>
            <AksiNyataCTACard
              href={
                `/pelatihan-mandiri/aksi-nyata/191?` +
                stringify({
                  topik_id: '1',
                  topik_name: 'merdeka belajar',
                })
              }
              title="Membangun lingkungan belajar masa depan yang aman dan inklusif."
              author="Manja Mulyani Siregar"
              image="https://storage.googleapis.com/microlearning-public-production/image/collection/quill-editor/a9bb8348-21db-43c5-9cc7-a4e1df0a5a6a-image-1.jpg"
              aksiNyataId={'191'}
              order={1}
            />
            <AksiNyataCTACard
              href={
                `/pelatihan-mandiri/aksi-nyata/98?` +
                stringify({
                  topik_id: '2',
                  topik_name: 'disiplin positif',
                })
              }
              title="Praktik Budaya Positif: Keyakinan Kelas dan Restitusi."
              author="Sandy Atmaja Lauwoie"
              image="https://storage.googleapis.com/microlearning-public-production/image/collection/quill-editor/5c025b7b-5870-429e-beef-edb39053e388-image-2.jpg"
              aksiNyataId={'98'}
              order={2}
            />
            <AksiNyataCTACard
              href={
                `/pelatihan-mandiri/aksi-nyata/559?` +
                stringify({
                  topik_id: '6',
                  topik_name: 'kurikulum',
                })
              }
              title="Apa Kata Siswa Tentang Perubahan Kurikulum?"
              author="Nur Ilmi Setianingsih"
              image="https://storage.googleapis.com/microlearning-public-production/image/collection/quill-editor/7c97367a-10bd-4b14-bcae-207a1bd0813e-aksinyata-3.jpg"
              aksiNyataId={'559'}
              order={3}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TopicListCallToAction
